import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Code2, Sun, Moon, LogOut, User, Zap, Star, Trophy, Target,
  ArrowLeft, Flame, ChevronRight, BarChart3, Clock, Award,
  BookOpen, Atom, FlaskConical, Brain, Cpu, Wrench, Pickaxe,
  Mountain, Microscope, ShieldCheck, Heart, Lightbulb, Wallet,
  Briefcase, ScrollText, Triangle, Globe, Landmark, CircuitBoard,
  Leaf, Bot, Dna, Gamepad2, Rocket, Users, GraduationCap,
  PenLine, Settings2, Building2, DollarSign, Beaker, Scissors,
  Wind, MapPin, Search, Check
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { labProgressApi } from '../lib/api';

const LAB_META = {
  physics: { title: 'Physics Lab', icon: Atom, color: '#f59e0b', totalMissions: 4 },
  chemistry: { title: 'Chemistry Lab', icon: FlaskConical, color: '#22c55e', totalMissions: 4 },
  'ai-prompt': { title: 'AI Prompt Lab', icon: Brain, color: '#8b5cf6', totalMissions: 3 },
  drone: { title: 'Drone Simulator', icon: Rocket, color: '#06b6d4', totalMissions: 4 },
  sorting: { title: 'Sorting Algorithms', icon: BarChart3, color: '#ec4899', totalMissions: 4 },
  geometry: { title: 'Geometry Playground', icon: Triangle, color: '#f97316', totalMissions: 4 },
  algebra: { title: 'Algebra Lab', icon: Target, color: '#6366f1', totalMissions: 4 },
  history: { title: 'History Timeline', icon: Clock, color: '#ca8a04', totalMissions: 4 },
  geography: { title: 'Geography Map Quiz', icon: Globe, color: '#0d9488', totalMissions: 4 },
  electrical: { title: 'Electrical Circuits', icon: CircuitBoard, color: '#eab308', totalMissions: 4 },
  english: { title: 'English Language', icon: BookOpen, color: '#e11d48', totalMissions: 7 },
  sanskrit: { title: 'Sanskrit Language', icon: ScrollText, color: '#d97706', totalMissions: 7 },
  'computer-science': { title: 'Computer Science', icon: Cpu, color: '#64748b', totalMissions: 6 },
  plumbing: { title: 'Plumbing Workshop', icon: Wrench, color: '#0ea5e9', totalMissions: 6 },
  mining: { title: 'Mining & Resources', icon: Pickaxe, color: '#b45309', totalMissions: 6 },
  metallurgy: { title: 'Metallurgy Lab', icon: Mountain, color: '#71717a', totalMissions: 6 },
  microbiology: { title: 'Microbiology Explorer', icon: Microscope, color: '#ec4899', totalMissions: 6 },
  'road-safety': { title: 'Road Safety', icon: ShieldCheck, color: '#dc2626', totalMissions: 6 },
  'civic-manners': { title: 'Civic Manners', icon: Heart, color: '#d946ef', totalMissions: 6 },
  'life-skills': { title: 'Life Skills', icon: Lightbulb, color: '#65a30d', totalMissions: 6 },
  financial: { title: 'Financial Planning', icon: Wallet, color: '#059669', totalMissions: 6 },
  career: { title: 'Career Explorer', icon: Briefcase, color: '#4f46e5', totalMissions: 6 },
  'competitive-exam': { title: 'Competitive Exam Prep', icon: Trophy, color: '#dc2626', totalMissions: 6 },
  'advanced-math': { title: 'Advanced Math', icon: BarChart3, color: '#4f46e5', totalMissions: 6 },
  biology: { title: 'Biology Lab', icon: Dna, color: '#16a34a', totalMissions: 6 },
  botany: { title: 'Botany Lab', icon: Leaf, color: '#15803d', totalMissions: 6 },
  robotics: { title: 'Robotics Lab', icon: Bot, color: '#6366f1', totalMissions: 6 },
  'political-science': { title: 'Political Science', icon: Landmark, color: '#b91c1c', totalMissions: 6 },
  // --- 10 Advanced Custom Labs ---
  'chem-reaction-sim': { title: 'Chemical Reactions', icon: Beaker, color: '#22c55e', totalMissions: 10, advanced: true },
  'virtual-dissection': { title: 'Virtual Dissection', icon: Scissors, color: '#ef4444', totalMissions: 10, advanced: true },
  'physics-sim': { title: 'Physics Simulator', icon: Atom, color: '#3b82f6', totalMissions: 10, advanced: true },
  'earth-explorer': { title: 'Earth Explorer', icon: Globe, color: '#10b981', totalMissions: 10, advanced: true },
  'math-visualizer': { title: 'Math Visualizer', icon: PenLine, color: '#8b5cf6', totalMissions: 10, advanced: true },
  'engineering-sim': { title: 'Engineering Machines', icon: Settings2, color: '#f97316', totalMissions: 10, advanced: true },
  'body-explorer': { title: 'Human Body', icon: Heart, color: '#ef4444', totalMissions: 10, advanced: true },
  'city-builder': { title: 'City Builder', icon: Building2, color: '#06b6d4', totalMissions: 10, advanced: true },
  'finance-sim': { title: 'Finance Simulator', icon: DollarSign, color: '#10b981', totalMissions: 10, advanced: true },
  'history-explorer': { title: 'History Time Machine', icon: Clock, color: '#f59e0b', totalMissions: 10, advanced: true },
};

const ADVANCED_LAB_IDS = Object.keys(LAB_META).filter(k => LAB_META[k].advanced);

const BADGE_TIERS = [
  { min: 1, label: 'Bronze Explorer', color: '#cd7f32', bg: 'from-amber-700 to-yellow-800' },
  { min: 3, label: 'Silver Scholar', color: '#c0c0c0', bg: 'from-gray-400 to-gray-500' },
  { min: 5, label: 'Gold Master', color: '#ffd700', bg: 'from-yellow-400 to-amber-500' },
  { min: 8, label: 'Platinum Expert', color: '#e5e4e2', bg: 'from-indigo-300 to-violet-400' },
  { min: 10, label: 'Diamond Legend', color: '#b9f2ff', bg: 'from-cyan-300 to-blue-400' },
];

export default function LabProgressPage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('my'); // 'my' | 'class'
  const [classData, setClassData] = useState(null);
  const [classLoading, setClassLoading] = useState(false);
  const [search, setSearch] = useState('');

  const isTeacher = user?.role === 'super_admin' || user?.role === 'org_admin' || user?.role === 'faculty';

  useEffect(() => {
    labProgressApi.getStats().then(data => {
      setStats(data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'class' && isTeacher && !classData) {
      setClassLoading(true);
      labProgressApi.getClassOverview().then(data => {
        setClassData(data);
      }).catch(() => {}).finally(() => setClassLoading(false));
    }
  }, [tab, isTeacher, classData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const totalXp = stats?.total_xp || 0;
  const missionsCompleted = stats?.total_missions_completed || 0;
  const labsStarted = stats?.labs_started || 0;
  const labsCompleted = stats?.labs_completed || 0;
  const perLab = stats?.per_lab || {};

  const level = Math.floor(totalXp / 200) + 1;
  const xpProgress = ((totalXp % 200) / 200) * 100;

  // Advanced labs completion count
  const advancedCompleted = ADVANCED_LAB_IDS.filter(id => {
    const labData = perLab[id];
    return labData && labData.missions >= 10;
  }).length;

  const currentBadge = [...BADGE_TIERS].reverse().find(b => advancedCompleted >= b.min);

  // All labs with progress
  const labsWithProgress = Object.entries(perLab)
    .map(([labId, data]) => ({ labId, ...data, meta: LAB_META[labId] }))
    .filter(l => l.meta)
    .sort((a, b) => b.xp - a.xp);

  const advancedLabsProgress = labsWithProgress.filter(l => l.meta.advanced);
  const regularLabsProgress = labsWithProgress.filter(l => !l.meta.advanced);

  const startedIds = new Set(Object.keys(perLab));
  const unstartedAdvanced = ADVANCED_LAB_IDS.filter(id => !startedIds.has(id));
  const unstartedRegular = Object.entries(LAB_META).filter(([id, m]) => !startedIds.has(id) && !m.advanced);

  // Filtered class data
  const filteredStudents = classData?.students?.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.email.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="lab-progress-page">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
      </div>

      <header className="relative z-10 glass border-b border-border/50 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">CognitiaIndia</span>
            </Link>
            <div className="h-6 w-px bg-border hidden md:block" />
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground">Dashboard</Link>
              <Link to="/labs" className="text-sm font-medium text-muted-foreground hover:text-foreground">Visual Labs</Link>
              <Link to="/lab-progress" className="text-sm font-medium text-foreground">My Progress</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <span className="text-sm hidden md:inline">{user?.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>Dashboard</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs-progress">
            <ArrowLeft className="w-4 h-4 mr-1" /> Labs
          </Button>
        </div>

        {/* Tab Toggle (only for teachers) */}
        {isTeacher && (
          <div className="flex gap-2 mb-6" data-testid="tab-toggle">
            <Button variant={tab === 'my' ? 'default' : 'outline'} size="sm" onClick={() => setTab('my')} data-testid="tab-my">
              <User className="w-4 h-4 mr-1" /> My Progress
            </Button>
            <Button variant={tab === 'class' ? 'default' : 'outline'} size="sm" onClick={() => setTab('class')} data-testid="tab-class">
              <Users className="w-4 h-4 mr-1" /> Class Overview
            </Button>
          </div>
        )}

        {/* ═══════ MY PROGRESS VIEW ═══════ */}
        {tab === 'my' && (
          <>
            <h1 className="text-3xl font-bold mb-2" data-testid="progress-title">My Lab Journey</h1>
            <p className="text-muted-foreground mb-8">Track your progress, earn XP, and level up across all labs.</p>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-xl" />)}
              </div>
            ) : (
              <>
                {/* Level & XP Banner */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="mb-8 p-6 rounded-2xl border bg-gradient-to-r from-primary/5 via-card to-yellow-500/5 border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/20">
                        <span className="text-2xl font-black text-white">{level}</span>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Current Level</div>
                        <div className="text-xl font-bold">Explorer Level {level}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <span className="text-lg font-bold text-yellow-400">{totalXp} XP</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Level {level}</span>
                      <span>{totalXp % 200}/{200} XP to next level</span>
                    </div>
                    <Progress value={xpProgress} className="h-2.5" />
                  </div>
                </motion.div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                    className="stat-card" data-testid="stat-missions">
                    <div className="stat-card-icon bg-primary/10 mb-3"><Target className="w-6 h-6 text-primary" /></div>
                    <div className="text-2xl font-bold">{missionsCompleted}</div>
                    <div className="text-sm text-muted-foreground">Missions Done</div>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="stat-card" data-testid="stat-labs-started">
                    <div className="stat-card-icon bg-blue-500/10 mb-3"><Flame className="w-6 h-6 text-blue-500" /></div>
                    <div className="text-2xl font-bold">{labsStarted}</div>
                    <div className="text-sm text-muted-foreground">Labs Started</div>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                    className="stat-card" data-testid="stat-labs-complete">
                    <div className="stat-card-icon bg-green-500/10 mb-3"><Trophy className="w-6 h-6 text-green-500" /></div>
                    <div className="text-2xl font-bold">{labsCompleted}</div>
                    <div className="text-sm text-muted-foreground">Labs Completed</div>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="stat-card" data-testid="stat-xp">
                    <div className="stat-card-icon bg-yellow-500/10 mb-3"><Zap className="w-6 h-6 text-yellow-500" /></div>
                    <div className="text-2xl font-bold">{totalXp}</div>
                    <div className="text-sm text-muted-foreground">Total XP</div>
                  </motion.div>
                </div>

                {/* ── Advanced Labs: Badges & Certificates ── */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                  className="mb-8 p-6 rounded-2xl border bg-card" data-testid="badges-section">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-400" /> Advanced Lab Badges
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">Complete all 10 missions in an advanced lab to earn its badge</p>
                    </div>
                    {currentBadge && (
                      <div className={`px-4 py-2 rounded-full bg-gradient-to-r ${currentBadge.bg} text-white font-bold text-sm shadow-lg`}
                        data-testid="current-tier">
                        {currentBadge.label}
                      </div>
                    )}
                  </div>

                  {/* Badge Tier Progress */}
                  <div className="flex items-center gap-2 mb-5">
                    {BADGE_TIERS.map((tier, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                          advancedCompleted >= tier.min ? 'scale-110' : 'opacity-30 grayscale'
                        }`}
                          style={{ borderColor: tier.color, color: tier.color, backgroundColor: advancedCompleted >= tier.min ? tier.color + '20' : 'transparent' }}
                          title={tier.label}>
                          {tier.min}
                        </div>
                        {i < BADGE_TIERS.length - 1 && (
                          <div className={`w-8 h-0.5 rounded ${advancedCompleted >= BADGE_TIERS[i + 1].min ? 'bg-amber-400' : 'bg-border'}`} />
                        )}
                      </div>
                    ))}
                    <span className="text-xs text-muted-foreground ml-2">{advancedCompleted}/10 labs mastered</span>
                  </div>

                  {/* Individual Lab Badges */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="badge-grid">
                    {ADVANCED_LAB_IDS.map(labId => {
                      const meta = LAB_META[labId];
                      const labData = perLab[labId];
                      const missions = labData?.missions || 0;
                      const isComplete = missions >= 10;
                      const pct = Math.round((missions / 10) * 100);
                      const Icon = meta.icon;
                      return (
                        <motion.div key={labId} whileHover={{ scale: 1.03 }}
                          onClick={() => navigate(`/labs/${labId}`)}
                          className={`relative p-4 rounded-xl border cursor-pointer transition-all ${
                            isComplete ? 'border-amber-500/40 bg-gradient-to-b from-amber-500/10 to-transparent shadow-lg shadow-amber-500/5' :
                            missions > 0 ? 'border-border/50 bg-card hover:border-primary/30' :
                            'border-border/20 bg-card/50 opacity-50 hover:opacity-80'
                          }`}
                          data-testid={`badge-${labId}`}>
                          {isComplete && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                              <Check className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                          <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center"
                            style={{ background: isComplete ? meta.color + '30' : meta.color + '10' }}>
                            <Icon className="w-5 h-5" style={{ color: meta.color }} />
                          </div>
                          <p className="text-xs font-semibold text-center truncate">{meta.title}</p>
                          <div className="mt-2">
                            <Progress value={pct} className="h-1.5" />
                            <p className="text-[10px] text-muted-foreground text-center mt-1">{missions}/10</p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Certificate Banner */}
                {advancedCompleted >= 10 && (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 border border-amber-500/30 text-center"
                    data-testid="certificate-banner">
                    <Award className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                    <h3 className="text-lg font-bold text-amber-300">Certificate of Excellence</h3>
                    <p className="text-sm text-muted-foreground mt-1">You have mastered all 10 Advanced Labs!</p>
                    <p className="text-xs text-amber-500/60 mt-1">Diamond Legend Achievement Unlocked</p>
                  </motion.div>
                )}

                {/* Advanced Labs In Progress */}
                {advancedLabsProgress.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Advanced Labs In Progress</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {advancedLabsProgress.map((lab, i) => {
                        const Icon = lab.meta.icon;
                        const total = lab.meta.totalMissions;
                        const done = lab.missions;
                        const pct = Math.round((done / total) * 100);
                        const isComplete = done >= total;
                        return (
                          <motion.div key={lab.labId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="p-5 rounded-xl border bg-card hover:border-primary/30 transition-all cursor-pointer group"
                            onClick={() => navigate(`/labs/${lab.labId}`)}
                            data-testid={`progress-${lab.labId}`}>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: lab.meta.color + '20' }}>
                                <Icon className="w-5 h-5" style={{ color: lab.meta.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">{lab.meta.title}</h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">{done}/{total} missions</span>
                                  {isComplete && <Badge variant="outline" className="text-[10px] text-green-400 border-green-500/30">Complete</Badge>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-xs font-bold text-yellow-400">
                                <Zap className="w-3.5 h-3.5" /> {lab.xp}
                              </div>
                            </div>
                            <Progress value={pct} className="h-2" />
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-muted-foreground">{pct}% complete</span>
                              <div className="flex gap-0.5">
                                {Array.from({ length: total }, (_, mi) => (
                                  <div key={mi} className={`w-2 h-2 rounded-full ${(lab.completed_ids || []).includes(mi + 1) ? 'bg-green-500' : 'bg-muted'}`} />
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Not Started Advanced Labs */}
                {unstartedAdvanced.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Advanced Labs — Not Yet Started ({unstartedAdvanced.length})</h2>
                    <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {unstartedAdvanced.map(labId => {
                        const meta = LAB_META[labId];
                        const Icon = meta.icon;
                        return (
                          <div key={labId}
                            className="p-4 rounded-xl border border-border/30 bg-card/50 hover:border-primary/20 transition-all cursor-pointer opacity-60 hover:opacity-100"
                            onClick={() => navigate(`/labs/${labId}`)}>
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.color + '15' }}>
                                <Icon className="w-4 h-4" style={{ color: meta.color }} />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium">{meta.title}</h4>
                                <span className="text-xs text-muted-foreground">{meta.totalMissions} missions</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Regular Labs In Progress */}
                {regularLabsProgress.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Regular Labs In Progress</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {regularLabsProgress.map((lab, i) => {
                        const Icon = lab.meta.icon;
                        const total = lab.meta.totalMissions;
                        const done = lab.missions;
                        const pct = Math.round((done / total) * 100);
                        const isComplete = done >= total;
                        return (
                          <motion.div key={lab.labId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="p-5 rounded-xl border bg-card hover:border-primary/30 transition-all cursor-pointer group"
                            onClick={() => navigate(`/labs/${lab.labId}`)}
                            data-testid={`progress-${lab.labId}`}>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: lab.meta.color + '20' }}>
                                <Icon className="w-5 h-5" style={{ color: lab.meta.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">{lab.meta.title}</h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">{done}/{total} missions</span>
                                  {isComplete && <Badge variant="outline" className="text-[10px] text-green-400 border-green-500/30">Complete</Badge>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-xs font-bold text-yellow-400">
                                <Zap className="w-3.5 h-3.5" /> {lab.xp}
                              </div>
                            </div>
                            <Progress value={pct} className="h-2" />
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Not Started Regular Labs */}
                {unstartedRegular.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold mb-4">Regular Labs — Not Yet Started ({unstartedRegular.length})</h2>
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {unstartedRegular.map(([labId, meta]) => {
                        const Icon = meta.icon;
                        return (
                          <div key={labId}
                            className="p-4 rounded-xl border border-border/30 bg-card/50 hover:border-primary/20 transition-all cursor-pointer opacity-60 hover:opacity-100"
                            onClick={() => navigate(`/labs/${labId}`)}>
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.color + '15' }}>
                                <Icon className="w-4 h-4" style={{ color: meta.color }} />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium">{meta.title}</h4>
                                <span className="text-xs text-muted-foreground">{meta.totalMissions} missions</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {missionsCompleted === 0 && (
                  <div className="text-center py-16 rounded-xl bg-card border border-border">
                    <Award className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                    <h3 className="text-lg font-semibold mb-2">No progress yet</h3>
                    <p className="text-muted-foreground mb-4">Start a lab to begin your learning journey!</p>
                    <Button onClick={() => navigate('/labs')} className="rounded-full" data-testid="start-first-lab">
                      <Zap className="w-4 h-4 mr-2" /> Explore Labs
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ═══════ CLASS OVERVIEW (Teacher) ═══════ */}
        {tab === 'class' && isTeacher && (
          <>
            <h1 className="text-3xl font-bold mb-2" data-testid="class-title">Class Lab Progress</h1>
            <p className="text-muted-foreground mb-6">See all students' lab completion status at a glance.</p>

            {classLoading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
              </div>
            ) : classData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl border bg-card" data-testid="class-stat-students">
                    <div className="text-2xl font-bold">{classData.total_students}</div>
                    <div className="text-sm text-muted-foreground">Total Students</div>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <div className="text-2xl font-bold text-amber-400">
                      {classData.students?.filter(s => s.labs_completed >= 10).length || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">All Labs Mastered</div>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <div className="text-2xl font-bold text-blue-400">
                      {Math.round(classData.students?.reduce((a, s) => a + s.total_missions, 0) / Math.max(classData.total_students, 1))}
                    </div>
                    <div className="text-sm text-muted-foreground">Avg Missions/Student</div>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <div className="text-2xl font-bold text-green-400">
                      {Math.round(classData.students?.reduce((a, s) => a + s.total_xp, 0) / Math.max(classData.total_students, 1))}
                    </div>
                    <div className="text-sm text-muted-foreground">Avg XP/Student</div>
                  </div>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="text" placeholder="Search students..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-xl border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="class-search" />
                </div>

                {/* Student Table */}
                <div className="rounded-2xl border bg-card overflow-hidden" data-testid="class-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left px-4 py-3 font-semibold">Student</th>
                          <th className="text-center px-3 py-3 font-semibold">Grade</th>
                          <th className="text-center px-3 py-3 font-semibold">XP</th>
                          <th className="text-center px-3 py-3 font-semibold">Missions</th>
                          <th className="text-center px-3 py-3 font-semibold">Labs</th>
                          {/* Advanced lab columns */}
                          {ADVANCED_LAB_IDS.map(id => (
                            <th key={id} className="text-center px-2 py-3" title={LAB_META[id].title}>
                              {React.createElement(LAB_META[id].icon, { className: 'w-3.5 h-3.5 mx-auto', style: { color: LAB_META[id].color } })}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((student, i) => (
                          <tr key={student.user_id} className="border-b border-border/30 hover:bg-muted/10 transition-colors">
                            <td className="px-4 py-3">
                              <div className="font-medium text-sm">{student.name}</div>
                              <div className="text-xs text-muted-foreground">{student.email}</div>
                            </td>
                            <td className="text-center px-3 py-3 text-xs text-muted-foreground">{student.grade || '-'}</td>
                            <td className="text-center px-3 py-3">
                              <span className="font-semibold text-yellow-400">{student.total_xp}</span>
                            </td>
                            <td className="text-center px-3 py-3">{student.total_missions}</td>
                            <td className="text-center px-3 py-3">{student.labs_started}</td>
                            {ADVANCED_LAB_IDS.map(id => {
                              const labProg = student.per_lab?.[id];
                              const missions = labProg?.missions || 0;
                              const complete = missions >= 10;
                              return (
                                <td key={id} className="text-center px-2 py-3">
                                  {missions === 0 ? (
                                    <span className="text-[10px] text-muted-foreground/30">-</span>
                                  ) : complete ? (
                                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mx-auto">
                                      <Check className="w-3 h-3 text-white" />
                                    </div>
                                  ) : (
                                    <span className="text-[10px] font-mono text-muted-foreground">{missions}/10</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {filteredStudents.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      {search ? 'No students match your search' : 'No student lab data yet'}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-muted-foreground">Failed to load class data</div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
